import type { InfiniteData } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';

import { LARGE_DROPDOWN_PAGE_SIZE } from '../../constants';
import { PresentationLabelQueryKeys } from '../config/queryKeys';
import { getRequestHeaders } from '../lib/headers';
import { LegacyCoopClient } from '../lib/LegacyCoopClient';
import { createPaginationQueryOptions } from '../lib/paginationOptions';
import {
  type PresentationLabelListResponse,
  PresentationLabelListResponseSchema,
} from '../lib/schemas';
import type { PresetUseInfiniteQueryOptions } from '../lib/types';
import type { PaginationVariables } from '../types/paginationVariables';
import type { SearchVariables } from '../types/searchVariables';

export type PresentationLabelInfiniteListQueryVariables = Omit<
  PaginationVariables & SearchVariables,
  'skip'
>;

export function usePresentationLabelInfiniteList<
  TData = PresentationLabelListResponse,
>(
  variables: PresentationLabelInfiniteListQueryVariables,
  options?: PresetUseInfiniteQueryOptions<
    PresentationLabelListResponse,
    InfiniteData<TData>
  >,
) {
  const session = useWmgSession();
  const { take = LARGE_DROPDOWN_PAGE_SIZE, ...restVariables } = variables;

  const { data, ...query } = useInfiniteQuery({
    queryKey: PresentationLabelQueryKeys.infiniteList(variables),
    queryFn: async ({ pageParam }) => {
      const responseData = await LegacyCoopClient.get('labels/presentation', {
        headers: getRequestHeaders(session.token),
        searchParams: { ...pageParam, ...restVariables },
      }).json<PresentationLabelListResponse>();

      // convert skip/take pagination to page/totalPages pagination
      return PresentationLabelListResponseSchema.parse({
        data: responseData.data,
        total: responseData.total,
        page: responseData.total === 0 ? 0 : (pageParam.skip ?? 0) / take + 1,
        totalPages: Math.ceil(responseData.total / take),
      });
    },
    placeholderData: keepPreviousData,
    ...createPaginationQueryOptions({ take }),
    ...options,
  });

  return { presentationLabelInfiniteList: data, ...query };
}
